import React from "react";

import { validNumber } from "@utils/calculators";
import { getDamPath } from "@utils/configuration/aem";

import { useGranite } from "@redux/modules/value/translations/translations.provider";

import ResultField from "@components/Calculator/ResultField/resultField.component";
import ResultTitle from "@components/Calculator/ResultTitle/resultTitle.component";
import { Col } from "@components/General/Grid/col.component";
import { Row } from "@components/General/Grid/row.component";

type Props = {
    roomArea: number;
    tileArea: number;
    packageArea: number;
    mailParams: any;
};

const Result = ({ roomArea, tileArea, packageArea, mailParams }: Props) => {
    const { t } = useGranite();

    const nrOfTilesNeeded = () => {
        const nrOfTiles = Number(Math.ceil(roomArea / tileArea));

        return validNumber(nrOfTiles) ? nrOfTiles : 0;
    };
    // 5% loss
    const nrOfTilesNeeded5PercentLoss = (nrOfTilesNeeded: number) => {
        const nrOfTilesNeeded5PercentLoss = Number(nrOfTilesNeeded * 1.05);

        return validNumber(nrOfTilesNeeded5PercentLoss)
            ? nrOfTilesNeeded5PercentLoss
            : 0;
    };
    const tileArea5PercentLoss = (nrOfTilesNeeded5PercentLoss: number) => {
        const tileArea5PercentLoss = Number(
            nrOfTilesNeeded5PercentLoss * tileArea,
        );

        return validNumber(tileArea5PercentLoss) ? tileArea5PercentLoss : 0;
    };
    const nrOfBoxes5PercentLoss = (tileArea5PercentLoss: number) => {
        const nrOfBoxes5PercentLoss = Number(
            Math.ceil(tileArea5PercentLoss / packageArea),
        );

        return validNumber(nrOfBoxes5PercentLoss) ? nrOfBoxes5PercentLoss : 0;
    };
    // 10% loss
    const nrOfTilesNeeded10PercentLoss = (nrOfTilesNeeded: number) => {
        const nrOfTilesNeeded10PercentLoss = Number(nrOfTilesNeeded * 1.1);

        return validNumber(nrOfTilesNeeded10PercentLoss)
            ? nrOfTilesNeeded10PercentLoss
            : 0;
    };
    const tileArea10PercentLoss = (nrOfTilesNeeded10PercentLoss: number) => {
        const tileArea10PercentLoss = Number(
            nrOfTilesNeeded10PercentLoss * tileArea,
        );

        return validNumber(tileArea10PercentLoss) ? tileArea10PercentLoss : 0;
    };
    const nrOfBoxes10PercentLoss = (tileArea10PercentLoss: number) => {
        const nrOfBoxes10PercentLoss = Number(
            Math.ceil(tileArea10PercentLoss / packageArea),
        );

        return validNumber(nrOfBoxes10PercentLoss) ? nrOfBoxes10PercentLoss : 0;
    };
    // glue
    const glueNeededFloor = () => {
        const glueNeededFloor = roomArea * 3;

        return validNumber(glueNeededFloor) ? glueNeededFloor : 0;
    };
    const glueNeededWall = () => {
        const glueNeededWall = roomArea * 2;

        return validNumber(glueNeededWall) ? glueNeededWall : 0;
    };
    // joint cement
    const jointCementNeededFloor = () => {
        const jointCementNeededFloor = roomArea / 2;

        return validNumber(jointCementNeededFloor) ? jointCementNeededFloor : 0;
    };
    const jointCementNeededWall = () => {
        const jointCementNeededWall = roomArea / 2;

        return validNumber(jointCementNeededWall) ? jointCementNeededWall : 0;
    };

    return (
        <div className={"mt-4"}>
            <ResultTitle mailParams={mailParams} />
            <Row>
                <ResultField
                    copy={t("calculator.tileCalculator.roomArea")}
                    value={roomArea.toFixed(2)}
                    unit={"m"}
                    sup={2}
                    md={6}
                    lg={3}
                />
                <ResultField
                    copy={t("calculator.tileCalculator.tileArea")}
                    value={tileArea.toFixed(2)}
                    unit={"m"}
                    sup={2}
                    md={6}
                    lg={3}
                />
            </Row>
            <Row>
                <Col sm={6} className={"mt-4"}>
                    <Row>
                        <Col sm={12}>
                            <h4 className="d-sm-block">
                                {t(
                                    "calculator.tileCalculator.amountTilesClassic",
                                )}
                            </h4>
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={12} md={6}>
                            <Row>
                                <ResultField
                                    copy={t(
                                        "calculator.tileCalculator.nrOfTiles",
                                    )}
                                    value={nrOfTilesNeeded5PercentLoss(
                                        nrOfTilesNeeded(),
                                    ).toFixed(0)}
                                />
                            </Row>
                            <Row>
                                <ResultField
                                    copy={t(
                                        "calculator.tileCalculator.areaOfTiles",
                                    )}
                                    value={tileArea5PercentLoss(
                                        nrOfTilesNeeded5PercentLoss(
                                            nrOfTilesNeeded(),
                                        ),
                                    ).toFixed(2)}
                                    unit={"m"}
                                    sup={2}
                                />
                            </Row>
                            <Row>
                                <ResultField
                                    copy={t(
                                        "calculator.tileCalculator.nrOfBoxes",
                                    )}
                                    value={nrOfBoxes5PercentLoss(
                                        tileArea5PercentLoss(
                                            nrOfTilesNeeded5PercentLoss(
                                                nrOfTilesNeeded(),
                                            ),
                                        ),
                                    ).toFixed(0)}
                                />
                            </Row>
                        </Col>
                        <Col sm={12} md={6}>
                            <img
                                className={"my-2 w-100"}
                                src={`${getDamPath()}/hulp-en-advies/calculators/tegel-klassiek.jpg`}
                                alt={"tegel-klassiek.jpg"}
                            />
                        </Col>
                    </Row>
                </Col>
                <Col sm={6} className={"mt-4"}>
                    <Row>
                        <Col sm={12}>
                            <h4 className="d-sm-block">
                                {t("calculator.tileCalculator.amountTiles")}
                            </h4>
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={12} md={6}>
                            <Row>
                                <ResultField
                                    copy={t(
                                        "calculator.tileCalculator.nrOfTiles",
                                    )}
                                    value={nrOfTilesNeeded10PercentLoss(
                                        nrOfTilesNeeded(),
                                    ).toFixed(0)}
                                />
                            </Row>
                            <Row>
                                <ResultField
                                    copy={t(
                                        "calculator.tileCalculator.areaOfTiles",
                                    )}
                                    value={tileArea10PercentLoss(
                                        nrOfTilesNeeded10PercentLoss(
                                            nrOfTilesNeeded(),
                                        ),
                                    ).toFixed(2)}
                                    unit={"m"}
                                    sup={2}
                                />
                            </Row>
                            <Row>
                                <ResultField
                                    copy={t(
                                        "calculator.tileCalculator.nrOfBoxes",
                                    )}
                                    value={nrOfBoxes10PercentLoss(
                                        tileArea10PercentLoss(
                                            nrOfTilesNeeded10PercentLoss(
                                                nrOfTilesNeeded(),
                                            ),
                                        ),
                                    ).toFixed(0)}
                                />
                            </Row>
                        </Col>
                        <Col sm={12} md={6}>
                            <img
                                className={"my-2 w-100"}
                                src={`${getDamPath()}/hulp-en-advies/calculators/tegel-overhoeks.jpg`}
                                alt={"tegel-overhoeks.jpg"}
                            />
                        </Col>
                    </Row>
                </Col>
                <Col sm={6} className={"mt-4"}>
                    <Row>
                        <Col sm={12}>
                            <h4 className="d-sm-block">
                                {t(
                                    "calculator.tileCalculator.glueAndJoiningForFloorTiles",
                                )}
                            </h4>
                        </Col>
                    </Row>
                    <Row>
                        <ResultField
                            copy={t("calculator.tileCalculator.AmountOfGlue")}
                            value={glueNeededFloor().toFixed(0)}
                            unit={"kg"}
                            sm={12}
                            lg={6}
                        />
                    </Row>
                    <Row>
                        <ResultField
                            copy={t(
                                "calculator.tileCalculator.AmountOfJoining",
                            )}
                            value={jointCementNeededFloor().toFixed(0)}
                            unit={"kg"}
                            sm={12}
                            lg={6}
                        />
                    </Row>
                </Col>
                <Col sm={6} className={"mt-4"}>
                    <Row>
                        <Col sm={12}>
                            <h4 className="d-sm-block">
                                {t(
                                    "calculator.tileCalculator.glueAndJoiningForWallTiles",
                                )}
                            </h4>
                        </Col>
                    </Row>
                    <Row>
                        <ResultField
                            copy={t("calculator.tileCalculator.AmountOfGlue")}
                            value={glueNeededWall().toFixed(0)}
                            unit={"kg"}
                            sm={12}
                            lg={6}
                        />
                    </Row>
                    <Row>
                        <ResultField
                            copy={t(
                                "calculator.tileCalculator.AmountOfJoining",
                            )}
                            value={jointCementNeededWall().toFixed(0)}
                            unit={"kg"}
                            sm={12}
                            lg={6}
                        />
                    </Row>
                </Col>
            </Row>
        </div>
    );
};

export default Result;
